<!--
  User Menu 组件
  负责显示用户菜单和登录状态
  只消费 props，通过事件上抛操作
-->
<template>
  <div class="user-menu">
    <!-- 用户未登录时显示登录按钮 -->
    <template v-if="!isLoggedIn">
      <el-button type="primary" @click="showLoginDialog = true">登录</el-button>
      <LoginDialog
        v-model="showLoginDialog"
        @login-success="handleLoginSuccess"
      />
    </template>

    <!-- 用户已登录时显示用户信息 -->
    <template v-else>
      <el-dropdown trigger="hover" :hide-on-click="false" :show-arrow="false" :offset="12">
        <div class="user-info">
          <el-avatar :size="32" :src="userInfo?.avatar || defaultAvatar">
            {{ userInfo?.username?.charAt(0)?.toUpperCase() }}
          </el-avatar>
          <el-icon class="dropdown-arrow"><ArrowDown /></el-icon>
        </div>
        <template #dropdown>
          <div class="modern-user-dropdown">
            <!-- 用户头像和基本信息 -->
            <div class="user-profile-section">
              <div class="user-avatar-wrapper">
                <el-avatar :size="56" :src="userInfo?.avatar || defaultAvatar" class="user-avatar">
                  {{ userInfo?.username?.charAt(0)?.toUpperCase() }}
                </el-avatar>
                <div class="user-status-dot"></div>
              </div>
              <div class="user-basic-info">
                <div class="user-name">{{ userInfo?.username }}</div>
                <div class="user-role-badge">{{ userRoleLabel }}</div>
              </div>
            </div>

            <!-- 配额信息卡片 -->
            <div class="quota-card">
              <div class="quota-header">
                <div class="quota-icon">
                  <el-icon><Star /></el-icon>
                </div>
                <div class="quota-title">今日配额</div>
              </div>
              <div class="quota-content">
                <div class="quota-numbers">
                  <span class="quota-used">{{ quotaUsed }}</span>
                  <span class="quota-separator">/</span>
                  <span class="quota-total">{{ quotaLimit }}</span>
                </div>
                <div class="quota-progress">
                  <div class="quota-progress-bar" :style="{ width: quotaPercentage + '%' }"></div>
                </div>
              </div>
            </div>

            <!-- 快捷操作 -->
            <div class="quick-actions">
              <div class="action-item" @click="$emit('go-to-profile')">
                <div class="action-icon">
                  <el-icon><User /></el-icon>
                </div>
                <span class="action-text">个人中心</span>
              </div>

              <!-- 主题切换 - 暂时注释，后续可能启用 -->
              <!-- <div class="action-item" @click="$emit('theme-toggle')">
                <div class="action-icon">
                  <el-icon>
                    <Sunny v-if="isDarkTheme"/>
                    <Moon v-else/>
                  </el-icon>
                </div>
                <span class="action-text">{{ isDarkTheme ? '浅色模式' : '深色模式' }}</span>
              </div> -->

              <!-- 分割线 -->
              <!-- <div class="menu-divider"></div> -->

              <!-- 帮助文档 -->
              <div class="action-item" @click="$emit('show-guide')">
                <div class="action-icon">
                  <el-icon><QuestionFilled /></el-icon>
                </div>
                <span class="action-text">查看引导</span>
              </div>

              <!-- API 官网 -->
              <div class="action-item" @click="$emit('open-api-site')">
                <div class="action-icon">
                  <el-icon><Link /></el-icon>
                </div>
                <span class="action-text">API 官网</span>
              </div>

              <!-- 分割线 -->
              <div class="menu-divider"></div>

              <div class="action-item logout-action" @click="$emit('logout')">
                <div class="action-icon">
                  <el-icon><SwitchButton /></el-icon>
                </div>
                <span class="action-text">退出登录</span>
              </div>
            </div>
          </div>
        </template>
      </el-dropdown>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from 'vue'
import {
  Star,
  User,
  SwitchButton,
  ArrowDown,
  // Sunny,
  // Moon,
  QuestionFilled,
  Link
} from '@element-plus/icons-vue'
import LoginDialog from './LoginDialog.vue'

interface Props {
  isLoggedIn: boolean
  userInfo?: {
    username: string
    avatar?: string
    role?: string
  }
  quotaUsed: number
  quotaLimit: number
  defaultAvatar: string
  isDarkTheme?: boolean
  isMobile?: boolean
}

const props = defineProps<Props>()

const emit = defineEmits<{
  'logout': []
  'go-to-profile': []
  'theme-toggle': []
  'show-guide': []
  'open-api-site': []
}>()

const showLoginDialog = ref(false)

const handleLoginSuccess = () => {
  showLoginDialog.value = false
  window.dispatchEvent(new CustomEvent('login-success'))
}

const handleGlobalShowLogin = () => {
  showLoginDialog.value = true
}

onMounted(() => {
  window.addEventListener('show-login-dialog', handleGlobalShowLogin)
})

onUnmounted(() => {
  window.removeEventListener('show-login-dialog', handleGlobalShowLogin)
})

// 计算属性
const userRoleLabel = computed(() => {
  return props.userInfo?.role || '普通用户'
})

const quotaPercentage = computed(() => {
  if (props.quotaLimit === 0) return 0
  return Math.min((props.quotaUsed / props.quotaLimit) * 100, 100)
})
</script>


<style scoped>
.user-menu {
  display: flex;
  align-items: center;
}

.user-info {
  display: flex;
  align-items: center;
  gap: var(--tts-space-2);
  padding: 4px var(--tts-space-2);
  border-radius: var(--tts-radius-base);
  cursor: pointer;
  transition: all 0.3s ease;
}

.user-info:hover {
  background-color: var(--tts-bg-secondary);
}

.user-info:focus-visible {
  outline: 2px solid var(--tts-primary);
  outline-offset: 2px;
}

.dropdown-arrow {
  font-size: 12px;
  color: var(--tts-text-secondary);
  transition: transform 0.3s ease;
}

.user-info:hover .dropdown-arrow {
  color: var(--tts-text-primary);
}

.username {
  font-size: var(--tts-font-sm);
  font-weight: 500;
  color: var(--tts-text-primary);
}

.modern-user-dropdown {
  padding: var(--tts-space-4);
  min-width: 280px;
  background: var(--tts-bg-elevated);
  border-radius: var(--tts-radius-lg);
  box-shadow: var(--tts-shadow-lg);
  border: 1px solid var(--tts-border-base);
}

.user-profile-section {
  display: flex;
  align-items: center;
  gap: var(--tts-space-3);
  margin-bottom: var(--tts-space-4);
}

.user-avatar-wrapper {
  position: relative;
}

.user-status-dot {
  position: absolute;
  bottom: 2px;
  right: 2px;
  width: 12px;
  height: 12px;
  background: var(--tts-success);
  border: 2px solid var(--tts-bg-elevated);
  border-radius: 50%;
}

.user-basic-info {
  flex: 1;
}

.user-name {
  font-size: var(--tts-font-base);
  font-weight: 600;
  color: var(--tts-text-primary);
  margin-bottom: 4px;
}

.user-role-badge {
  font-size: var(--tts-font-xs);
  color: var(--tts-text-secondary);
  background: var(--tts-bg-secondary);
  padding: 2px 8px;
  border-radius: 12px;
  display: inline-block;
}

.quota-card {
  background: var(--tts-bg-secondary);
  border-radius: var(--tts-radius-base);
  padding: var(--tts-space-3);
  margin-bottom: var(--tts-space-4);
}

.quota-header {
  display: flex;
  align-items: center;
  gap: var(--tts-space-2);
  margin-bottom: var(--tts-space-2);
}

.quota-icon {
  color: var(--tts-warning);
}

.quota-title {
  font-size: var(--tts-font-sm);
  font-weight: 500;
  color: var(--tts-text-primary);
}

.quota-numbers {
  display: flex;
  align-items: baseline;
  gap: 4px;
  margin-bottom: var(--tts-space-2);
}

.quota-used {
  font-size: var(--tts-font-lg);
  font-weight: 600;
  color: var(--tts-primary);
}

.quota-separator {
  color: var(--tts-text-secondary);
}

.quota-total {
  font-size: var(--tts-font-sm);
  color: var(--tts-text-secondary);
}

.quota-progress {
  height: 4px;
  background: var(--tts-border-base);
  border-radius: 2px;
  overflow: hidden;
}

.quota-progress-bar {
  height: 100%;
  background: linear-gradient(90deg, var(--tts-primary), var(--tts-primary-300));
  transition: width 0.3s ease;
}

.quick-actions {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.action-item {
  display: flex;
  align-items: center;
  gap: var(--tts-space-2);
  padding: 8px 12px;
  border-radius: var(--tts-radius-sm);
  cursor: pointer;
  transition: all 0.3s ease;
  color: var(--tts-text-primary);
}

.action-item:hover {
  background: var(--tts-bg-secondary);
}

.action-item:focus-visible {
  outline: 2px solid var(--tts-primary);
  outline-offset: 2px;
}

.action-item.logout-action:hover {
  background: var(--tts-error-light);
  color: var(--tts-error);
}

.action-item.version-item {
  cursor: default;
  opacity: 0.7;
}

.action-item.version-item:hover {
  background: transparent;
}

.action-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
}

.action-text {
  font-size: var(--tts-font-sm);
  font-weight: 500;
}

.menu-divider {
  height: 1px;
  background: var(--tts-border-light);
  margin: var(--tts-space-2) 0;
}

@media (max-width: 768px) {
  .modern-user-dropdown {
    min-width: 260px;
    padding: 12px;
  }
  
  .username {
    display: none;
  }
}
</style>
